import {useCallback} from 'react';
import {useStoredState} from '../shared/hooks/useStoredState';

/**
 * One key for the whole panel, shared across every client. The app has no server-side
 * per-person sort store — every persisted sort here is localStorage, i.e. per browser
 * profile, which is what "per-user" means elsewhere in this app. A key per client id has
 * no precedent and would grow localStorage unboundedly. Mirrors
 * `usePersistentTimesheetColumnSort`'s 'time-page-sort-ascending'.
 */
export const CONTACTS_SORT_ASCENDING_STORAGE_KEY = 'client-contacts-sort-ascending';

export type ContactSortDirection = {
	/** true = A-Z. This is a two-state toggle — there is no third "unsorted" state. */
	ascending: boolean;
	toggle: () => void;
};

export function useContactSortDirection(): ContactSortDirection {
	// The initialiser overload rather than a plain default value: `useStoredState`'s
	// `stringSafeParse` turns an unparseable entry into a *string*, which is truthy and would
	// silently read as "ascending". Coerce anything that is not a boolean back to the default
	// explicitly.
	const [ascending, setAscending] = useStoredState<boolean>(CONTACTS_SORT_ASCENDING_STORAGE_KEY, stored =>
		typeof stored === 'boolean' ? stored : true
	);

	const toggle = useCallback(() => setAscending(prev => !prev), [setAscending]);

	return {ascending, toggle};
}
